import React, { useState } from 'react';
import './GanttChart.css';

function GanttChart({ tasks, onUpdate, readOnly = false }) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!tasks || tasks.length === 0) return null;

  const handleCellClick = (taskId, field, currentValue) => {
    if (readOnly) return;
    setEditingCell({ taskId, field });
    
    // Para o campo cost, pegar o valor bruto (sem formatação)
    if (field === 'cost') {
      const task = tasks.find(t => t.id === taskId);
      const rawValue = task?.cost || task?.custo || '';
      setEditValue(rawValue);
    } else {
      setEditValue(currentValue || '');
    }
  };

  const handleCellBlur = () => {
    if (editingCell && onUpdate) {
      const { taskId, field } = editingCell;
      const updatedTasks = tasks.map(task => 
        task.id === taskId ? { ...task, [field]: editValue } : task
      );
      onUpdate(updatedTasks);
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // #211: date-only ("2026-05-19") parseado cru vira meia-noite UTC →
    // -3h em America/Sao_Paulo = exibia o DIA ANTERIOR. Âncora em meia-noite
    // LOCAL (mesmo idioma do NCEditor pra concluida_em).
    const date = /^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))
      ? new Date(dateStr + 'T00:00:00')
      : new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    
    // Se for string de texto (ex: "A definir", "Incluído"), retornar como está
    if (typeof value === 'string' && isNaN(parseFloat(value))) {
      return value;
    }
    
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const renderCell = (task, field, displayValue) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === field;
    
    if (isEditing) {
      return (
        <input
          type={field === 'cost' ? 'number' : field.includes('Date') ? 'date' : 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCellBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="gantt-cell-input"
        />
      );
    }

    return (
      <div 
        className="gantt-cell-content"
        onClick={() => handleCellClick(task.id, field, task[field])}
        title={readOnly ? "" : "Clique para editar"}
      >
        {displayValue}
      </div>
    );
  };

  return (
    <div className="gantt-container-new">
      <table className="gantt-table">
        <thead>
          <tr>
            <th className="gantt-th">Tarefa</th>
            <th className="gantt-th">Responsável</th>
            <th className="gantt-th">Data Início</th>
            <th className="gantt-th">Data Fim</th>
            <th className="gantt-th">Investimento</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task, index) => {
            // Suportar tanto 'cost' quanto 'custo' para compatibilidade
            const cost = task.cost || task.custo || 'A definir';
            
            return (
              <tr key={task.id || index} className="gantt-row-new">
                <td className="gantt-td gantt-td-task">
                  {renderCell(task, 'name', task.name)}
                </td>
                <td className="gantt-td">
                  {renderCell(task, 'owner', task.owner)}
                </td>
                <td className="gantt-td">
                  {renderCell(task, 'start', formatDate(task.start))}
                </td>
                <td className="gantt-td">
                  {renderCell(task, 'end', formatDate(task.end))}
                </td>
                <td className="gantt-td gantt-td-cost">
                  {renderCell(task, 'cost', formatCurrency(cost))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default GanttChart;
